import { useState, useMemo } from "react";
import { useColumnLayout, isHideable, ColumnsMenu, NamedViewsMenu } from "./ColumnLayout";

const EMPTY_COLS = [];
import { CaretUp, CaretDown, ArrowsDownUp, MagnifyingGlass, X, FunnelSimple } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const acc = (col) => col.accessor || ((row) => row[col.key]);
const ALL = "__all__";
const optList = (c) =>
  (c.options || []).map((o) => (o && typeof o === "object" ? o : { value: String(o), label: String(o) }));

function cmp(av, bv, numeric) {
  if (numeric) return (Number(av) || 0) - (Number(bv) || 0);
  return String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true, sensitivity: "base" });
}

// Generic Business-Central style list table: click-to-sort column headers plus a
// single search box that filters across every searchable column. Sort/search can be
// internal (default) or controlled via the `controls` prop ({ sort, setSort, filters, setFilters }),
// where `filters` is a plain object `{ q: "..." }` (kept object-shaped so Saved Views persist it).
export function DataTable({
  data = [], columns, rowKey, rowTestid, rowProps, empty = "Nothing here yet.",
  testid, filterable = true, controls, leading, footer, loading, loadingText = "Loading…",
  searchPlaceholder = "Search…", toolbarExtra, viewKey, extraColumns = EMPTY_COLS, namedViews = false,
}) {
  const [localSort, setLocalSort] = useState(null);
  const [localFilters, setLocalFilters] = useState({});
  const sort = controls ? controls.sort : localSort;
  const setSort = controls ? controls.setSort : setLocalSort;
  const rawFilters = controls ? controls.filters : localFilters;
  const filters = useMemo(() => rawFilters || {}, [rawFilters]);
  const setFilters = controls ? controls.setFilters : setLocalFilters;

  const query = filters.q || "";
  const setQuery = (v) => setFilters({ ...filters, q: v });
  const colVals = useMemo(() => filters.cols || {}, [filters]);
  const setColVal = (key, v) => {
    const next = { ...colVals };
    if (v && v !== ALL) next[key] = v; else delete next[key];
    setFilters({ ...filters, cols: next });
  };

  const allCols = useMemo(() => [...columns, ...extraColumns.filter((e) => !columns.some((c) => c.key === e.key))], [columns, extraColumns]);
  const { layout: savedLayout, saveLayout } = useColumnLayout(viewKey);
  const defaultKeys = useMemo(() => columns.filter(isHideable).map((c) => c.key), [columns]);
  const layoutKeys = (viewKey && (filters.layout || savedLayout)) || defaultKeys;
  const visibleCols = useMemo(() => {
    if (!viewKey) { return columns; }
    const byKey = Object.fromEntries(allCols.map((c) => [c.key, c]));
    return [...layoutKeys.map((k) => byKey[k]).filter((c) => c && isHideable(c)), ...columns.filter((c) => !isHideable(c))];
  }, [viewKey, columns, allCols, layoutKeys]);
  const setLayout = (keys) => { setFilters({ ...filters, layout: keys }); saveLayout(keys); };
  const resetLayout = () => { const { layout, ...rest } = filters; setFilters(rest); saveLayout(null); };

  const colMap = useMemo(() => Object.fromEntries(allCols.map((c) => [c.key, c])), [allCols]);
  const searchCols = useMemo(
    () => visibleCols.filter((c) => c.filter === "text" || c.filter === "select"),
    [visibleCols],
  );
  const selectCols = useMemo(
    () => visibleCols.filter((c) => c.filter === "select" && (c.options || []).length > 0),
    [visibleCols],
  );
  const activeColFilters = Object.keys(colVals).filter((k) => colVals[k]).length;
  const showToolbar = (filterable && (searchCols.length > 0 || selectCols.length > 0)) || !!toolbarExtra || !!viewKey;

  const view = useMemo(() => {
    let out = data;
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        searchCols.some((c) => String(acc(c)(r) ?? "").toLowerCase().includes(q)),
      );
    }
    for (const [k, v] of Object.entries(colVals)) {
      if (!v) continue;
      const col = colMap[k];
      if (!col) continue;
      out = out.filter((r) => String(acc(col)(r) ?? "") === String(v));
    }
    if (sort?.key && colMap[sort.key]) {
      const col = colMap[sort.key];
      const get = acc(col);
      out = [...out].sort((a, b) => {
        const r = cmp(get(a), get(b), !!col.numeric);
        return sort.dir === "desc" ? -r : r;
      });
    }
    return out;
  }, [data, query, colVals, sort, colMap, searchCols]);

  const toggleSort = (key) => {
    const next = !sort || sort.key !== key
      ? { key, dir: "asc" }
      : sort.dir === "asc" ? { key, dir: "desc" } : null;
    setSort(next);
  };

  const span = visibleCols.length + (leading ? 1 : 0);
  const alignCls = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "");

  return (
    <div className="border border-border bg-card">
      {showToolbar && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border flex-wrap">
          {searchCols.length > 0 && filterable && (
            <div className="relative w-full max-w-xs">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 pl-8 pr-8 text-sm rounded-sm"
                data-testid={testid ? `${testid}-search` : "table-search"}
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid={testid ? `${testid}-search-clear` : "table-search-clear"}
                  aria-label="Clear search"
                >
                  <X size={13} weight="bold" />
                </button>
              )}
            </div>
          )}
          {filterable && selectCols.map((c) => (
            <Select key={c.key} value={colVals[c.key] || ALL} onValueChange={(v) => setColVal(c.key, v)}>
              <SelectTrigger
                className={`h-8 w-auto min-w-[8.5rem] text-sm rounded-sm gap-1.5 ${colVals[c.key] ? "border-primary text-primary" : ""}`}
                data-testid={testid ? `${testid}-filter-${c.key}` : `filter-${c.key}`}
              >
                <FunnelSimple size={13} className={colVals[c.key] ? "text-primary" : "text-muted-foreground"} />
                <SelectValue placeholder={c.header} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid={`filter-${c.key}-all`}>All {c.header}</SelectItem>
                {optList(c).map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`filter-${c.key}-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          {(query || activeColFilters > 0) && (
            <>
              <span className="text-xs text-muted-foreground tabular">{view.length} match{view.length === 1 ? "" : "es"}</span>
              {activeColFilters > 0 && (
                <button
                  onClick={() => setFilters({ ...filters, cols: {} })}
                  className="text-xs text-primary hover:underline"
                  data-testid={testid ? `${testid}-filters-clear` : "table-filters-clear"}
                >
                  Clear filters
                </button>
              )}
            </>
          )}
          {viewKey && (
            <div className="ml-auto flex items-center gap-1.5">
              {namedViews && <NamedViewsMenu viewKey={viewKey} filters={{ ...filters, layout: layoutKeys }} sort={sort} onApply={(v) => { setFilters(v.filters || {}); setSort(v.sort || null); }} />}
              <ColumnsMenu allCols={allCols} visibleKeys={layoutKeys} onChange={setLayout} onReset={resetLayout} />
            </div>
          )}
          {toolbarExtra && <div className={viewKey ? "" : "ml-auto"}>{toolbarExtra(view)}</div>}
        </div>
      )}
      <Table data-testid={testid}>
        <TableHeader>
          <TableRow>
            {leading && <TableHead className={leading.headClassName || "w-8"}>{leading.header ? leading.header() : null}</TableHead>}
            {visibleCols.map((c) => {
              const active = sort?.key === c.key;
              const sortable = c.sortable !== false;
              return (
                <TableHead
                  key={c.key}
                  onClick={sortable ? () => toggleSort(c.key) : undefined}
                  data-testid={sortable ? `sort-${c.key}` : undefined}
                  className={`text-xs uppercase tracking-wider ${alignCls(c.align)} ${c.headClassName || ""} ${sortable ? "cursor-pointer select-none hover:text-primary transition-colors" : ""}`}
                >
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : c.align === "center" ? "justify-center w-full" : ""}`}>
                    {c.header}
                    {sortable && c.header ? (
                      active
                        ? (sort.dir === "asc"
                          ? <CaretUp size={11} weight="bold" className="text-primary" />
                          : <CaretDown size={11} weight="bold" className="text-primary" />)
                        : <ArrowsDownUp size={11} className="opacity-30" />
                    ) : null}
                  </span>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && (
            <TableRow><TableCell colSpan={span} className="text-center py-10 text-muted-foreground">{loadingText}</TableCell></TableRow>
          )}
          {!loading && view.length === 0 && (
            <TableRow><TableCell colSpan={span} className="text-center py-10 text-muted-foreground">{(query || activeColFilters) ? "No matches for your search or filters." : empty}</TableCell></TableRow>
          )}
          {!loading && view.map((row) => {
            const rp = rowProps ? rowProps(row) : {};
            return (
              <TableRow key={rowKey(row)} data-testid={rowTestid} {...rp} className={`hover:bg-secondary/60 ${rp.className || ""}`}>
                {leading && <TableCell className={leading.cellClassName || "w-8"}>{leading.cell ? leading.cell(row) : null}</TableCell>}
                {visibleCols.map((c) => (
                  <TableCell key={c.key} className={`${alignCls(c.align)} ${c.cellClassName || ""}`}>
                    {c.cell ? c.cell(row) : acc(c)(row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {typeof footer === "function" ? footer(view) : footer}
        </TableBody>
      </Table>
    </div>
  );
}
